// How the backoffice shows show times: in the browser's time zone, grouped by festival night.

/** A festival night runs from noon to noon, so a set at 02:00 on Saturday belongs to Friday night */
const NIGHT_STARTS_AT = 12;

const pad = (value: number) => String(value).padStart(2, '0');

/** The local calendar date, as YYYY-MM-DD */
export const toDateInputValue = (date: Date): string =>
  `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;

/** The local time, as HH:MM */
export const toTimeInputValue = (date: Date): string => `${pad(date.getHours())}:${pad(date.getMinutes())}`;

/** The same hour and minute on another local day (YYYY-MM-DD), or null if the value isn't a date. Drops seconds. */
export const withDate = (date: Date, value: string): Date | null => {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (!match) return null;
  const moved = new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]), date.getHours(), date.getMinutes());
  return isNaN(moved.getTime()) ? null : moved;
};

/** The same day at another local time (HH:MM), or null if the value isn't a time. Drops seconds. */
export const withTime = (date: Date, value: string): Date | null => {
  const match = /^(\d{2}):(\d{2})$/.exec(value);
  if (!match) return null;
  const moved = new Date(date);
  moved.setHours(Number(match[1]), Number(match[2]), 0, 0);
  return isNaN(moved.getTime()) ? null : moved;
};

/** The night a show starting at this moment belongs to, as the YYYY-MM-DD of its evening */
export const nightOf = (date: Date): string => {
  const evening = new Date(date);
  if (evening.getHours() < NIGHT_STARTS_AT) evening.setDate(evening.getDate() - 1);
  return toDateInputValue(evening);
};

/** When a night (YYYY-MM-DD) starts: noon of that day, local time */
export const nightStart = (night: string): Date => {
  const [year, month, day] = night.split('-').map(Number);
  return new Date(year, month - 1, day, NIGHT_STARTS_AT);
};

/** "Fri 26", or "ven. 26" in a French locale */
export const formatDay = (date: Date, locale = 'en-GB'): string =>
  `${date.toLocaleDateString(locale, { weekday: 'short' })} ${date.getDate()}`;

/** "20:15" */
export const formatTime = (date: Date): string => toTimeInputValue(date);

/** "Fri 26 · 23:00 – 01:00", with the end's day too when the show lasts a day or more */
export const formatShowTime = (start: Date, end: Date, locale?: string): string => {
  const endText = end.getTime() - start.getTime() >= 24 * 3600_000
    ? `${formatDay(end, locale)} · ${formatTime(end)}`
    : formatTime(end);
  return `${formatDay(start, locale)} · ${formatTime(start)} – ${endText}`;
};

/** "45 min", "2 h", "1 h 15 min" */
export const formatDuration = (start: Date, end: Date): string => {
  const minutes = Math.round((end.getTime() - start.getTime()) / 60_000);
  const hours = Math.floor(minutes / 60);
  if (hours === 0) return `${minutes} min`;
  return minutes % 60 === 0 ? `${hours} h` : `${hours} h ${minutes % 60} min`;
};
